import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { useGetAllLevAdminGroups } from '../../../queries/Admin/UserOperations/useGetAllLevAdminGroups';
import { type ISupportMailbox, useUpdateSupportMailboxMutation } from '../../../queries/Support/useSupportMailboxQuery';
import { getApiErrorMessage } from '../../../utils/Support/supportHelpers';
import { Modal } from '../../../web/components/Modal';
import { TrashIcon } from '../../../web/components/svgs/icons/TrashIcon';
import { Button } from '../../../web/components-v2/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../web/components-v2/Select';
import { THEME } from '../../../web/components-v2/Theme/theme';
import { styleSheet } from '../../components/accountDetails/ManageInternalRolesModal/styles';

const getFriendlyRoleName = (role: string) => (role.startsWith('Levitate:') ? role.slice('Levitate:'.length) : role);

const getFullRoleName = (role: string) => (role.includes(':') ? role : `Levitate:${role}`);

const getComparableRoleName = (role: string) => getFullRoleName(role).toLowerCase();

export function ManageMailboxPermissionsModal({ mailbox, onClose }: { mailbox: ISupportMailbox; onClose: () => void }) {
	const [selectedRole, setSelectedRole] = React.useState('');
	const [currentRoles, setCurrentRoles] = React.useState(mailbox.allowedRoles ?? []);
	const { data: allRoles, isLoading, isError } = useGetAllLevAdminGroups();
	const updateMailboxMutation = useUpdateSupportMailboxMutation(mailbox.id);

	React.useEffect(() => {
		setCurrentRoles(mailbox.allowedRoles ?? []);
	}, [mailbox.allowedRoles]);

	const currentRoleNames = new Set(currentRoles.map(getComparableRoleName));
	const availableRolesToAdd = (allRoles ?? []).filter(role => !currentRoleNames.has(getComparableRoleName(role)));

	const handleAddRole = async () => {
		if (!selectedRole) return;

		const fullRoleName = getFullRoleName(selectedRole);
		const nextRoles = [...currentRoles.map(getFullRoleName), fullRoleName];
		try {
			await updateMailboxMutation.mutateAsync({ allowedRoles: nextRoles });
			setCurrentRoles(nextRoles);
			toast.success(`Mailbox permission "${getFriendlyRoleName(fullRoleName)}" added successfully`);
			setSelectedRole('');
		} catch (error: unknown) {
			toast.error(`Failed to add mailbox permission: ${getApiErrorMessage(error, 'Unknown error')}`);
		}
	};

	const handleRemoveRole = async (roleName: string) => {
		const comparableRoleName = getComparableRoleName(roleName);
		const nextRoles = currentRoles
			.filter(role => getComparableRoleName(role) !== comparableRoleName)
			.map(getFullRoleName);
		try {
			await updateMailboxMutation.mutateAsync({ allowedRoles: nextRoles });
			setCurrentRoles(nextRoles);
			toast.success(`Mailbox permission "${getFriendlyRoleName(roleName)}" removed successfully`);
		} catch (error: unknown) {
			toast.error(`Failed to remove mailbox permission: ${getApiErrorMessage(error, 'Unknown error')}`);
		}
	};

	return (
		<Modal isOpen>
			<article className={css(styleSheet.modalContainer)}>
				<h3>Manage Mailbox Permissions for {mailbox.name}</h3>

				{isLoading ? (
					<div className={css(styleSheet.loadingContainer)}>
						<p>Loading permissions...</p>
					</div>
				) : (
					<>
						<div className={css(styleSheet.section)}>
							<h4>Current Permissions</h4>
							{currentRoles.length === 0 ? (
								<p className={css(styleSheet.emptyMessage)}>No role permissions assigned</p>
							) : (
								<ul className={css(styleSheet.rolesList)}>
									{currentRoles.map(role => (
										<li key={role} className={css(styleSheet.roleItem)}>
											<span className={css(styleSheet.roleName)}>{getFriendlyRoleName(role)}</span>
											<Button
												type='button'
												variant='ghost'
												size='icon'
												onClick={() => handleRemoveRole(role)}
												disabled={updateMailboxMutation.isPending}
												styles={[styleSheet.removeButton]}
												title={`Remove ${getFriendlyRoleName(role)}`}
												aria-label={`Remove ${getFriendlyRoleName(role)}`}
											>
												<TrashIcon fillColor={THEME.colors.danger.main} />
											</Button>
										</li>
									))}
								</ul>
							)}
						</div>

						<div className={css(styleSheet.section)}>
							<h4>Add Permission</h4>
							{isError ? (
								<p className={css(styleSheet.emptyMessage)}>Failed to load available role permissions</p>
							) : availableRolesToAdd.length === 0 ? (
								<p className={css(styleSheet.emptyMessage)}>All role permissions assigned</p>
							) : (
								<div className={css(styleSheet.addRoleContainer)}>
									<Select
										value={selectedRole}
										onValueChange={setSelectedRole}
										disabled={updateMailboxMutation.isPending}
									>
										<SelectTrigger styles={styleSheet.roleSelect}>
											<SelectValue placeholder='Select a role...' />
										</SelectTrigger>
										<SelectContent>
											{availableRolesToAdd.map(role => (
												<SelectItem key={role} value={role}>
													{getFriendlyRoleName(role)}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<Button
										onClick={handleAddRole}
										disabled={!selectedRole || updateMailboxMutation.isPending}
										isLoading={updateMailboxMutation.isPending}
										styles={[styleSheet.addButton]}
									>
										Add
									</Button>
								</div>
							)}
						</div>
					</>
				)}

				<div className={css(styleSheet.modalActions)}>
					<Button onClick={onClose} disabled={updateMailboxMutation.isPending} variant='secondary'>
						Close
					</Button>
				</div>
			</article>
		</Modal>
	);
}
